import {
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import DataDumpGetter from "../../../segments/DataDumpGetter/DataDumpGetter";
import type {
  DataDumpAggregateType,
  TimeRangeFinite,
} from "../../../types/types";
import {
  useGetPageFromPath,
  useTrendsPageFilterState,
  useTrendChartBinSize,
  useFilteredProcessIds,
  useShifts,
  useFocusRange,
  useInterval,
  useShouldApplyShiftRangesToDataDumps,
} from "../../../utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  DATA_DUMP_ROWS_QUERY,
  midgardClients,
} from "providers/MidgardProvider";
import useQueryWithErrorHandling from "utils/useQuery";
import { useDispatch } from "react-redux";
import {
  updateTargetRatesForTrendBars,
  UPDATE_DATA_DUMP_TREND_CHART_FOR_PROCESSES,
  UPDATE_MANY_DATA_DUMP_TREND_CHART_FOR_PROCESSES,
} from "providers/GlobalStateProvider";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import {
  CHECK_CURRENT_TREND_CHART_BARS_INTERVAL,
  CHECK_CURRENT_TREND_CHART_BARS_TRENDS_PAGE_INTERVAL,
} from "constants/intervals";
import {
  useFilteredShifts,
  useTrendChartBarRanges,
  useTrendChartBarsArray,
} from "utils/customHooks/trendChartHooks";
import { TrendChartBinSizesEnum } from "types/enums";
import { useCustomerTimeZoneOffsetMs } from "utils/timeZoneHooks";
import { DATA_DUMP_ROW_TARGET_TREND_LINE_TITLE } from "contexts/ErrorManager/errorManagerUtils";
import { useCleanRawDataDump } from "segments/DataDumpGetter/dataDumpGetterUtils";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { useProcessIdOnTier0Page } from "components/MeritTier0Page/tier0Utils";

/** renders one getter per trend chart bar
 *
 * rendered OUTSIDE of the trend chart bar itself to avoid rerender loops
 */
const TrendChartBarsDataDumpsGetter = () => {
  const { isTrendsPage, isTier0Page } = useGetPageFromPath();
  const trendChartBarsRangesTrendsPage = useTrendChartBarRanges();
  const shouldApplyShiftRangesToDataDumps =
    useShouldApplyShiftRangesToDataDumps();
  const filteredProcessIds = useFilteredProcessIds();
  const processIdTier0Page = useProcessIdOnTier0Page();
  const processIds =
    isTier0Page && processIdTier0Page
      ? [processIdTier0Page]
      : filteredProcessIds;
  const {
    trendChartBarsRanges: trendChartBarsRangesFromFocusRange,
    updatingNow,
  } = useTrendBarQueryRangesFromFocusRange();
  // on trends page, account for filtered-by-shift state
  const focusRange = useFocusRange();
  const shiftRanges = useFilteredShifts()
    .map(shift => shift.range)
    .filter(
      range =>
        // range within focusRange
        focusRange && range.ge >= focusRange.ge && range.lt <= focusRange.lt
    );
  const trendChartBarsRanges = useMemo(() => {
    if (shouldApplyShiftRangesToDataDumps) {
      return shiftRanges;
    } else if (isTrendsPage) {
      return trendChartBarsRangesTrendsPage;
    } else {
      return trendChartBarsRangesFromFocusRange;
    }
  }, [
    isTrendsPage,
    shouldApplyShiftRangesToDataDumps,
    shiftRanges,
    trendChartBarsRangesFromFocusRange,
    trendChartBarsRangesTrendsPage,
  ]);

  // dispatching the aggregates to the store causes a major re-render of many components.
  // so, to stabilize the render, we'll only dispatch once, when all the data is ready.
  const trendChartBarsDataDumpsRef = useRef<{
    bars: DataDumpAggregateType[];
    lastUpdate: number;
  }>({
    lastUpdate: Date.now(),
    bars: [],
  });
  // empty the bars whenever time range or process changes (these bars are now stale)
  const processIdOnSingleProcessPage = useProcessIdOnSingleProcessPage();

  useEffect(() => {
    trendChartBarsDataDumpsRef.current = {
      bars: [],
      lastUpdate: Date.now(),
    };
  }, [focusRange, processIdOnSingleProcessPage, processIdTier0Page]);
  const dispatch = useDispatch();
  const cleanRawDataDump = useCleanRawDataDump();
  useInterval({
    callback: () => {
      // check on an interval if we're ready to dispatch yet
      const allBarsPresent =
        trendChartBarsDataDumpsRef.current.bars.length ===
        trendChartBarsRanges.length;
      const newBarsPresent = trendChartBarsDataDumpsRef.current.bars.length > 0;
      const timeElapsed =
        Date.now() - trendChartBarsDataDumpsRef.current.lastUpdate;
      const ready =
        allBarsPresent || (newBarsPresent && timeElapsed > 6 * 1000);
      if (ready) {
        startTransition(() => {
          dispatch({
            type: UPDATE_MANY_DATA_DUMP_TREND_CHART_FOR_PROCESSES,
            payload: trendChartBarsDataDumpsRef.current.bars.map(dataDump =>
              cleanRawDataDump(dataDump, processIds)
            ),
          });
          trendChartBarsDataDumpsRef.current = {
            ...trendChartBarsDataDumpsRef.current,
            bars: [],
          };
        });
      }
    },
    delay: 2 * 1000,
    immediate: false,
  });
  const currentTrendBarRanges = useMemo(
    () => trendChartBarsRanges.filter(range => range.ge < updatingNow),
    [trendChartBarsRanges, updatingNow]
  );

  // restart the process when we change focus range (otherwise, we might dispatch some bars from a different focus range into the current focus range)
  return processIds.length === 0 ? null : (
    <>
      {currentTrendBarRanges.map(range => {
        // if it's the current bar, we'll refetch (re-render) whenever updatingNow updates
        const isCurrentBar = range.ge <= updatingNow && updatingNow < range.lt;
        return (
          <DataDumpGetterTrendChartBar
            // only use ge since lt on the current bar changes
            key={`${range.ge}-${JSON.stringify(processIds)}-${
              isCurrentBar ? updatingNow : ""
            }`}
            range={range}
            processIds={processIds}
            trendChartBarsDataDumpsRef={trendChartBarsDataDumpsRef}
          />
        );
      })}
    </>
  );
};

export default TrendChartBarsDataDumpsGetter;

/**
 * slice focusRange the same way as Askr aggregateOnFrequency: "DAILY":
 * focusRangeStart - midnight in customer's tz (e.g. 18:00 for France)
 * then midnight - midnight in customer's tz
 */
export function useTrendBarQueryRangesFromFocusRange(updateNow = true): {
  updatingNow: number;
  trendChartBarsRanges: TimeRangeFinite[];
} {
  const focusRange = useFocusRange();
  const { isTrendsPage, isTier0Page } = useGetPageFromPath();
  const rollupMetricTrendChartBarsArray = useTrendChartBarsArray();
  /** ! timezoneOffsetMs here is customerTimeZoneOffsetMs, don't confuse with timeZoneMsOffset = useTimeZoneMsOffset()  */
  const timezoneOffsetMs = useCustomerTimeZoneOffsetMs();
  const { trendChartBinSizeMs, trendChartBinSize } = useTrendChartBinSize();
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();

  const trendChartBarsRanges = useTrendChartBarRanges();
  const [now, setNow] = useState(Date.now());
  // check again every 10s
  useInterval({
    callback: () => {
      setNow(Date.now());
    },
    delay: !updateNow
      ? null
      : (isTrendsPage
          ? CHECK_CURRENT_TREND_CHART_BARS_TRENDS_PAGE_INTERVAL
          : CHECK_CURRENT_TREND_CHART_BARS_INTERVAL) *
        multiplyNetworkRequestIntervals,
    immediate: false,
  });

  return useMemo(() => {
    const isValidTimezoneOffsetMs = timezoneOffsetMs || timezoneOffsetMs === 0;
    if (
      !focusRange ||
      !isValidTimezoneOffsetMs ||
      focusRange.lt <= focusRange.ge
    ) {
      return { trendChartBarsRanges: [], updatingNow: now };
    }

    // for daily mode, we need to account for Askr splitting on the customer's midnight (below this block)
    // for other modes, bar ranges are simple:
    if (trendChartBinSize !== TrendChartBinSizesEnum.days) {
      return {
        trendChartBarsRanges,
        updatingNow: now,
      };
    }

    // generate the same time ranges as returned by Askr = split on midnight in the customer's timezone

    // first bar goes from focusRange.ge to midnight in the customer's timezone
    const firstMidnightLocal = new Date(
      focusRange.ge + trendChartBinSizeMs
    ).setHours(24, 0, 0, 0);
    const firstMidnightInCustomerTZ = firstMidnightLocal - timezoneOffsetMs;

    const firstBarRange = {
      ge: focusRange.ge,
      lt: firstMidnightInCustomerTZ,
    };

    const currentTrendBars = rollupMetricTrendChartBarsArray.filter(
      ({ range: { ge } }) => ge + timezoneOffsetMs < now
    );
    const lastBarLtUnclamped =
      currentTrendBars[currentTrendBars.length - 1]?.range.lt;
    const lastBarLt = isTier0Page
      ? Math.min(now, lastBarLtUnclamped)
      : lastBarLtUnclamped;

    const numBars = Math.ceil(
      (focusRange.lt - focusRange.ge) / trendChartBinSizeMs
    );
    const nextBarRanges = [...Array(numBars - 1)]
      .map((_, idx) => {
        const ge = firstBarRange.lt + idx * trendChartBinSizeMs;
        const lt = firstBarRange.lt + (idx + 1) * trendChartBinSizeMs;
        return {
          ge,
          lt: lastBarLt ? Math.min(lastBarLt, lt) : lt, // ensure last bar lt matches up with Askr aggregates
        };
      })
      .filter(({ ge, lt }) => ge < lt && ge <= now);

    // due to the above logic, firstBarRange can be >trendChartBinSizeMs depending on the time zone & focus range; if so, split it into two
    const firstBars =
      firstBarRange.lt - firstBarRange.ge > trendChartBinSizeMs
        ? [
            {
              ge: firstBarRange.ge,
              lt: firstBarRange.lt - trendChartBinSizeMs,
            },
            {
              ge: firstBarRange.lt - trendChartBinSizeMs,
              lt: firstBarRange.lt,
            },
          ]
        : [firstBarRange];
    return {
      trendChartBarsRanges: [...firstBars, ...nextBarRanges],
      updatingNow: now,
    };
  }, [
    focusRange,
    now,
    rollupMetricTrendChartBarsArray,
    timezoneOffsetMs,
    trendChartBarsRanges,
    trendChartBinSize,
    trendChartBinSizeMs,
    isTier0Page,
  ]);
}

// get the time range,
// OR if we're on trends page filteres by shift
// get the shift range instead
// (used for trend chart bars)
export const useRangeFilteredByShift = (
  range: TimeRangeFinite
): TimeRangeFinite => {
  const { isTrendsPage } = useGetPageFromPath();
  const { isTrendsPageFilteredByShift, trendsPageFilterState } =
    useTrendsPageFilterState();
  const shifts = useShifts();
  const { trendChartBinSizeMs } = useTrendChartBinSize();
  // if the trends page is filtered by shift,
  // we must use the range of the shift that this bar represents
  const timeRangeFilteredByShift =
    // only look if we're filtered by shift
    // if trends page is filtered by shift,
    // get output count for the shift range within this bar
    isTrendsPage && isTrendsPageFilteredByShift
      ? // find the FIRST matching shift
        shifts.find(
          shift =>
            // whose start time is gte bar start
            shift.range.ge >= range.ge &&
            // & whose end time is lt of the NEXT bar end
            // (since shifts often overlap the bar ranges)
            // e.g. the night shift, it will never be fully within one bar
            // so we show the count for the shift that's "on" (starts on) that day
            // instead of cutting the bars and shifts up and doing complex sums
            shift.range.lt < range.lt + trendChartBinSizeMs &&
            // & whose name matches the filter state name
            shift.shift.name === trendsPageFilterState.shiftName
        )?.range
      : range;

  return useMemo(
    () =>
      timeRangeFilteredByShift ||
      // if we don't find a shift, return a range anyway to render a bar
      range,
    [range, timeRangeFilteredByShift]
  );
};

function DataDumpGetterTrendChartBar({
  range,
  processIds,
  trendChartBarsDataDumpsRef,
}: {
  range: TimeRangeFinite;
  processIds: string[];
  trendChartBarsDataDumpsRef: React.MutableRefObject<{
    bars: DataDumpAggregateType[];
    lastUpdate: number;
  }>;
}) {
  const rangeAfterFilteredByShift = useRangeFilteredByShift(range);
  const isSingleProcessPage = processIds.length === 1;
  const isStorybook = useIsStorybook();
  const [flightConfig] = useFlightConfig();
  const showTargetTrendLine = flightConfig.trendChart.showTargetTrendLine;
  return isStorybook ? null : (
    <>
      <DataDumpGetter
        timeRange={{
          ge: rangeAfterFilteredByShift.ge,
          lt: rangeAfterFilteredByShift.lt,
        }}
        processIds={processIds}
        dataDumpAggType={UPDATE_DATA_DUMP_TREND_CHART_FOR_PROCESSES}
        retryUntilReceivedInterval={30 * 1000}
        customOnCompleted={data => {
          trendChartBarsDataDumpsRef.current = {
            ...trendChartBarsDataDumpsRef.current,
            bars: [
              ...(trendChartBarsDataDumpsRef.current.bars || []),
              data.dataDumpAggregate,
            ],
            lastUpdate: Date.now(),
          };
        }}
      />
      {isSingleProcessPage && showTargetTrendLine && (
        <TargetRatesPerTrendChartBarGetter
          timeRange={rangeAfterFilteredByShift}
          processId={processIds[0]}
        />
      )}
    </>
  );
}

/** on single-process pages with the target trend line,
 * populate the target rate at the start of each hour
 * to calculate the slope of the target trend line
 */
function TargetRatesPerTrendChartBarGetter({
  timeRange,
  processId,
}: {
  timeRange: TimeRangeFinite;
  processId: string;
}) {
  const ckey = useCustomerKey();
  const client = midgardClients.get(ckey);
  const dispatch = useDispatch();
  const [, startTransition] = useTransition();
  useQueryWithErrorHandling(
    DATA_DUMP_ROW_TARGET_TREND_LINE_TITLE,
    DATA_DUMP_ROWS_QUERY,
    {
      client,
      variables: {
        ge: String(timeRange.ge),
        lt: String(timeRange.lt),
        ckey,
        machemetryIds: [processId],
      },
      onCompleted: data => {
        const firstRowWithTargetRate = data?.dataDump?.find(
          row => row.targetRate
        );
        if (firstRowWithTargetRate) {
          startTransition(() => {
            dispatch(
              updateTargetRatesForTrendBars({
                processId,
                targetRate: firstRowWithTargetRate.targetRate,
                trendBarGe: timeRange.ge,
              })
            );
          });
        }
      },
    }
  );
  return null;
}
